import { createTheme } from '@mui/material/styles';

// SmartAI Africa Color Palette
const smartaiColors = {
  // Primary
  blue: '#046bd2',
  blueDark: '#0354a8',
  blueLight: '#3b8edf',

  // Secondary / Accent
  cyan: '#02f9fd',
  cyanMuted: '#2B9EB3',

  // Purple accent for gradients
  purple: '#354dc7',

  // Dark backgrounds (slate)
  darkBg: '#0f172a',
  darkPaper: '#1e293b',
  darkSurface: '#334155',

  // Light backgrounds
  lightBg: '#F0F5FA',
  lightPaper: '#FFFFFF',

  // Text
  darkTextPrimary: '#f1f5f9',
  darkTextSecondary: '#94a3b8',
  lightTextPrimary: '#1e293b',
  lightTextSecondary: '#334155',

  // Dividers
  darkDivider: 'rgba(255, 255, 255, 0.1)',
  lightDivider: '#D1D5DB',
};

// Signature gradient for primary buttons
const primaryGradient = `linear-gradient(135deg, ${smartaiColors.blue} 0%, ${smartaiColors.purple} 50%, ${smartaiColors.cyan} 100%)`;

// SmartAI Africa dark theme
export const smartaiThemeDark = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: smartaiColors.blue,
      dark: smartaiColors.blueDark,
      light: smartaiColors.blueLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: smartaiColors.cyan,
      light: '#66fbfe',
      dark: smartaiColors.cyanMuted,
      contrastText: '#0f172a',
    },
    background: {
      default: smartaiColors.darkBg,
      paper: smartaiColors.darkPaper,
    },
    text: {
      primary: smartaiColors.darkTextPrimary,
      secondary: smartaiColors.darkTextSecondary,
    },
    divider: smartaiColors.darkDivider,
    error: {
      main: '#ef4444',
    },
    warning: {
      main: '#f59e0b',
    },
    info: {
      main: smartaiColors.cyan,
    },
    success: {
      main: '#22c55e',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.5 },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${smartaiColors.darkSurface} ${smartaiColors.darkBg}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: smartaiColors.darkSurface,
            border: `2px solid ${smartaiColors.darkBg}`,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: smartaiColors.darkBg,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
          padding: '10px 24px',
          transition: 'all 0.2s ease-in-out',
        },
        containedPrimary: {
          background: primaryGradient,
          boxShadow: '0 3px 5px 2px rgba(4, 107, 210, .3)',
          '&:hover': {
            background: primaryGradient,
            filter: 'brightness(1.1)',
            boxShadow: '0 4px 8px 3px rgba(4, 107, 210, .4)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${smartaiColors.cyanMuted} 0%, ${smartaiColors.cyan} 100%)`,
          color: '#0f172a',
          boxShadow: '0 3px 5px 2px rgba(2, 249, 253, .2)',
          '&:hover': {
            background: `linear-gradient(135deg, ${smartaiColors.cyanMuted} 30%, ${smartaiColors.cyan} 100%)`,
            filter: 'brightness(1.1)',
            boxShadow: '0 4px 8px 3px rgba(2, 249, 253, .3)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: 'rgba(30, 41, 59, 0.8)',
          backgroundImage: 'none',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: smartaiColors.darkSurface,
              borderWidth: 1,
            },
            '&:hover fieldset': {
              borderColor: smartaiColors.blue,
            },
            '&.Mui-focused fieldset': {
              borderColor: smartaiColors.cyan,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: smartaiColors.darkPaper,
          backgroundImage: 'none',
          borderBottom: `1px solid ${smartaiColors.darkDivider}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: smartaiColors.cyan,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
  },
});

// SmartAI Africa light theme
export const smartaiThemeLight = createTheme({
  ...smartaiThemeDark,
  palette: {
    mode: 'light',
    primary: {
      main: smartaiColors.blue,
      dark: smartaiColors.blueDark,
      light: smartaiColors.blueLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: smartaiColors.cyanMuted,
      light: smartaiColors.cyan,
      dark: '#1e7a8a',
      contrastText: '#FFFFFF',
    },
    background: {
      default: smartaiColors.lightBg,
      paper: smartaiColors.lightPaper,
    },
    text: {
      primary: smartaiColors.lightTextPrimary,
      secondary: smartaiColors.lightTextSecondary,
    },
    divider: smartaiColors.lightDivider,
    error: {
      main: '#dc2626',
    },
    warning: {
      main: '#d97706',
    },
    info: {
      main: smartaiColors.blue,
    },
    success: {
      main: '#16a34a',
    },
  },
  components: {
    ...smartaiThemeDark.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${smartaiColors.lightDivider} ${smartaiColors.lightBg}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: smartaiColors.lightDivider,
            border: `2px solid ${smartaiColors.lightBg}`,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: smartaiColors.lightBg,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
          backdropFilter: 'none',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: `1px solid ${smartaiColors.lightDivider}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: smartaiColors.lightTextPrimary,
          borderBottom: `1px solid ${smartaiColors.lightDivider}`,
        },
      },
    },
  },
});
